import { type IActivityClientData, type IClientValueSummary, type IActivities, type ProviderActivitiesCountData, type ProviderActivitiesVars } from "./interfaces"
import { ACTIVITIES_COUNT_QUERY, ACTIVITIES_QUERY } from './queries';
import { useAuth } from "components/Auth";
import { useQuery } from "@apollo/client";
import { useQueryWithCacheExpiry } from "providers/Apollo/useQueryWithCacheExpiry";

export interface IUseProvider {
    allClients: IClientValueSummary[] | undefined,
    loading: boolean
}

export const mapActivityToListItem = (
    activity: IActivityClientData,
): IClientValueSummary => {
    const additionalParticipantsString = activity.additionalParticipants
            ?.map(participant => `${participant.firstName} ${participant.lastName}`)
            ?.join(', ');
        
    const additionalParticipantsLabel = additionalParticipantsString
            ? '(' + additionalParticipantsString + ')'
            : '';
    return {
        label: `${activity.client?.firstName} ${activity.client?.lastName} ${additionalParticipantsLabel}`,
        id: activity.activityId,
        ...activity,
    }
}

export const useProviderActivities = (providerId?: number): IUseProvider => {
    const { data: allClientsResponse, loading } = useQuery<IActivities, { providerId: number }>(ACTIVITIES_QUERY, {
        variables: { providerId: providerId ?? -1 },
        fetchPolicy: 'network-only',
    })

    const allClients: IClientValueSummary[] | undefined = 
        allClientsResponse?.activities?.map(activity => mapActivityToListItem(activity))
        .sort(function (client1, client2) {
            return client1.label.localeCompare(client2.label)
        }) || []

    return { allClients, loading }
}

export const useProviderActivitiesCount = (providerId?: number) => {
    const { effectiveProviderData } = useAuth();
    const resolvedProviderId = providerId ?? effectiveProviderData?.providerId;
    return useQueryWithCacheExpiry<ProviderActivitiesCountData, ProviderActivitiesVars>(ACTIVITIES_COUNT_QUERY, {
        variables: {
            providerId: resolvedProviderId ?? -1
        },
        fetchPolicy: 'cache-and-network',
        skip: !resolvedProviderId,
        cacheLife: 1800000 // cache for 30 minutes since this query is still painfully slow
    });
}
